import { CaretSortIcon, CheckIcon, PlusCircledIcon, UpdateIcon } from '@radix-ui/react-icons';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import sport_classes from '../../enums/sport_classes.json';
import api from '../../lib/api';
import { cn } from '../../lib/utils';
import { DatePickerSingle } from '../datePickerSingle';
import { Button } from '../ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../ui/command";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription, DialogHeader,
    DialogTitle,
    DialogTrigger
} from "../ui/dialog";
import { DropdownMenuItem } from "../ui/dropdown-menu";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../ui/use-toast';

const AddCompetitionSportClassDialog = ({ competition, fetchCompetition }) => {

    const [isLoading, setIsLoading] = useState(false);
    const form = useForm();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [open, setOpen] = useState(false);

    const [formData, setFormData] = useState({
        competitionId: competition.id,
        sportClassId: '',
        date: competition?.start_date.toLocaleString('sv').split('T')[0] || '',
        assessment: '',
        split: false,
    });

    useEffect(() => {
        if (!isDialogOpen) {
            form.reset({
                sportClassId: '',
                date: competition?.start_date.toLocaleString('sv').split('T')[0] || '',
                assessment: '',
            });
        }
    }, [isDialogOpen]);

    const getSportClassNameById = (id) => {
        const sportClass = sport_classes.find((sport_class) => sport_class.id === id);
        return sportClass ? sportClass.sport_class_name : "Okänd klass";
    };

    const handleDateChange = (date, name) => {
        if (!date) {

            return; // Early return if the date is null or undefined
        }


        setFormData(prevState => ({
            ...prevState,
            [name]: date.toLocaleString('sv').split(' ')[0], // Update the specific field (startDate, endDate, etc.)
        }));
    };

    const handleSubmit = async (data) => {
        setIsLoading(true);

        try {
            // Om vald klass inte är av typ höjd eller längd
            if (![13, 14, 15, 16, 17, 18].includes(formData.sportClassId)) {

                // Tvinga bedömning att vara icke tomt
                if (formData.assessment.length < 1) {
                    toast({
                        variant: 'destructive',
                        title: 'Något gick fel.',
                        description: "Fältet bedömningsform är obligatoriskt för banklasser.",
                    });
                    return;
                }
            }

            const response = await api.post(`https://skhrf-api.cmdigital.se/v1/competitions/create-class`, {
                ...formData
            });


            fetchCompetition();

            toast({
                title: 'Klass tillagd',
                description: `Klass ${getSportClassNameById(data.sportClassId)} blev tillagd för ${data.date}.`,
            });

            setIsDialogOpen(false);
        } catch (error) {
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error.response?.data?.error || "Kunde inte skicka förfrågan.",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const selectedSportClassId = form.watch('sportClassId');
    const hideAssessment = [13, 14, 15, 16, 17, 18].includes(Number(selectedSportClassId));

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                <DialogTrigger className='flex items-center w-full'>
                    <PlusCircledIcon className='mr-3'/>
                    Lägg till klass
                </DialogTrigger>
            </DropdownMenuItem>
            <DialogContent className="flex flex-col gap-6">
                <DialogHeader className='text-left'>
                    <DialogTitle>
                        Lägg till klass
                    </DialogTitle>
                    <DialogDescription>
                        Ange information för att lägga till klass.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
                        <FormField
                            control={form.control}
                            name="sportClassId"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel htmlFor='sportClassId'>Klass</FormLabel>
                                    <FormControl>
                                        <Popover modal={true} open={open} onOpenChange={setOpen}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    id='sportClassId'
                                                    variant="outline"
                                                    role="combobox"
                                                    className={cn(
                                                        "w-full justify-between",
                                                        !field.value && "text-muted-foreground"
                                                    )}
                                                >
                                                    {field.value
                                                        ? sport_classes.find(
                                                            (sport_class) => sport_class.id === field.value
                                                        )?.sport_class_name
                                                        : "Välj klass"}
                                                    <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="popover-content-width-full p-0">
                                                <Command>
                                                    <CommandInput placeholder="Sök klass..." />
                                                    <CommandList>
                                                        <CommandEmpty>Ingen klass hittades.</CommandEmpty>
                                                        <CommandGroup>
                                                            {sport_classes.map((sport_class) => (
                                                                <CommandItem
                                                                    className='cursor-pointer'
                                                                    value={sport_class.id}
                                                                    key={sport_class.id}
                                                                    onSelect={() => {
                                                                        setOpen(false);
                                                                        form.setValue("sportClassId", sport_class.id);
                                                                        form.setValue("assessment", "");
                                                                        setFormData(prev => ({
                                                                            ...prev,
                                                                            sportClassId: sport_class.id,
                                                                            assessment: ""
                                                                        }));
                                                                    }}
                                                                >
                                                                    <CheckIcon
                                                                        className={cn(
                                                                            "mr-2 h-4 w-4",
                                                                            sport_class.id === field.value
                                                                                ? "opacity-100"
                                                                                : "opacity-0"
                                                                        )}
                                                                    />
                                                                    {sport_class.sport_class_name}
                                                                </CommandItem>
                                                            ))}
                                                        </CommandGroup>
                                                    </CommandList>
                                                </Command>
                                            </PopoverContent>
                                        </Popover>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => (
                                <FormItem className='space-y-0 grid gap-2'>
                                    <FormLabel htmlFor='date'>Datum för klassen</FormLabel>
                                    <FormControl>
                                        <DatePickerSingle
                                            selectedDate={formData.date}
                                            id='date'
                                            onDateChange={(date) => handleDateChange(date, field.name)}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Render the assessment field only if the selected sport class id is not one of the hidden ones */}
                        {!hideAssessment && (
                            <FormField
                                control={form.control}
                                name="assessment"
                                render={({ field }) => (
                                    <FormItem className='space-y-0 grid gap-2'>
                                        <FormLabel htmlFor='assessment'>Bedömningsform</FormLabel>
                                        <FormControl>
                                            <Select
                                                onValueChange={(value) => {
                                                    field.onChange(value);
                                                    form.setValue("assessment", value);
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        assessment: value,
                                                    }));
                                                }}
                                                value={field.value}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="Välj bedömningsform" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {['A', 'B', 'C', 'D', 'E', 'F', 'SM'].map((assessment) => (
                                                        <SelectItem key={assessment} value={assessment}>
                                                            Bedömning {assessment}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                        <div className='grid grid-cols-2 gap-3'>
                            <DialogClose asChild>
                                <Button type='button' variant='secondary'>Avbryt</Button>
                            </DialogClose>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading ? <UpdateIcon className='loader-spinner' /> : "Lägg till"}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default AddCompetitionSportClassDialog;
